import { CommonModule } from '@angular/common';
import { Component, ElementRef, computed, effect, inject, signal, viewChild, viewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BatteryCharging, LucideAngularModule } from 'lucide-angular';
import { catchError, forkJoin, map, of } from 'rxjs';
import { BatteryConfiguration, BessForecastAsset, BessForecastBlock, BessForecastResponse, BessSchedule, BessSocLog, ForecastPriceSnapshot, ForecastPriceSnapshotDetail } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { BatteryConfigurationsService } from '../services/battery-configurations.service';
import { BessOperationsService } from '../services/bess-operations.service';

type ProductType = 'INTRADAY' | 'DAY_AHEAD';
type MarketProduct = 'DAM' | 'GDAM' | 'RTM';
type MatrixCell = {
  block: number;
  label: string;
  soc: number | null;
  power: number | null;
  action: 'charge' | 'discharge' | 'idle';
  trend: 'up' | 'down' | 'flat';
  bidPlaced: boolean;
  status: string;
  source: 'forecast' | 'actual';
};

type MatrixTooltip = {
  actionLabel: string;
  priceLabel: string;
  quantityLabel: string;
  socLabel: string;
  left: number;
  top: number;
};

type AssetMatrixRow = {
  asset: BatteryConfiguration;
  vendorLabel: string;
  currentSoc: number;
  currentBlockSoc: number;
  socStart: number;
  socEnd: number;
  socTrendPoints: string;
  socTrendAreaPath: string;
  totalChargeLakh: number;
  totalRevenueLakh: number;
  totalProfitLakh: number;
  cells: MatrixCell[];
};

@Component({
  standalone: true,
  selector: 'app-bess-fleet-operations-matrix-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './bess-fleet-operations-matrix.page.html',
  styleUrl: './bess-fleet-operations-matrix.page.scss',
})
export class BessFleetOperationsMatrixPage {
  private static readonly SERIAL_COLUMN_WIDTH = 56;
  private static readonly ASSET_COLUMN_WIDTH = 220;
  private static readonly DIVIDER_COLUMN_WIDTH = 42;

  private readonly batteryService = inject(BatteryConfigurationsService);
  private readonly operationsService = inject(BessOperationsService);
  private readonly toast = inject(ToastService);
  private readonly route = inject(ActivatedRoute);

  protected readonly BatteryChargingIcon = BatteryCharging;
  protected readonly loading = signal(false);
  protected readonly syncingForecastPrices = signal(false);
  protected readonly assets = signal<BatteryConfiguration[]>([]);
  protected readonly schedules = signal<BessSchedule[]>([]);
  protected readonly socLogs = signal<BessSocLog[]>([]);
  protected readonly forecasts = signal<BessForecastResponse | null>(null);
  protected readonly marketPrices = signal<Record<MarketProduct, Array<number | null>>>({ DAM: [], GDAM: [], RTM: [] });
  protected readonly linkedAssetId = signal<string | null>(null);
  protected readonly lastForecastPriceSyncLabel = signal<string>('');
  protected readonly hoveredTooltip = signal<MatrixTooltip | null>(null);
  private readonly filtersVersion = signal(0);
  private readonly scrollRequestVersion = signal(0);
  private readonly matrixScroll = viewChild<ElementRef<HTMLDivElement>>('matrixScroll');
  private readonly timeBlockHeaders = viewChildren<ElementRef<HTMLElement>>('timeBlockHeader');

  protected selectedProduct: ProductType = 'INTRADAY';
  protected thresholdInput = '';
  protected selectedDate = this.todayIsoDate();
  protected showSoc = true;
  protected showPower = false;
  protected showBidStatus = true;

  protected readonly blocks = computed(() => Array.from({ length: 96 }, (_, index) => index + 1));
  protected readonly hourGroups = computed(() => Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`));
  protected readonly minuteLabels = computed(() => Array.from({ length: 24 }, () => ['0', '15', '30', '45']).flat());
  protected readonly currentBlock = computed(() => this.resolveCurrentBlock());
  protected readonly currentWindowLabel = computed(() => this.blockLabel(this.currentBlock()));
  protected readonly marketBands = computed(() => {
    this.filtersVersion();
    return this.buildMarketBands();
  });
  protected readonly thresholdValue = computed(() => {
    this.filtersVersion();
    const value = Number(this.thresholdInput);
    return Number.isFinite(value) && value > 0 ? value : null;
  });
  protected readonly matrixRows = computed<AssetMatrixRow[]>(() => {
    this.filtersVersion();
    const selectedDate = this.selectedDate || this.todayIsoDate();
    const threshold = this.thresholdValue();
    const selectedForecastProduct = this.selectedMarketProduct();
    const lowBand = this.marketBandThreshold(selectedForecastProduct, 0.33);
    const highBand = this.marketBandThreshold(selectedForecastProduct, 0.67);
    const thresholdBenefits = this.buildThresholdBenefits(selectedForecastProduct);
    const forecastAssets = new Map<string, BessForecastAsset>(
      (this.forecasts()?.products?.[selectedForecastProduct] || []).map((asset) => [asset.battery_configuration_id, asset]),
    );
    const schedulesByAsset = new Map<string, Map<number, BessSchedule>>();
    const logsByAsset = new Map<string, Map<number, BessSocLog>>();

    for (const row of this.schedules().filter((item) => item.schedule_date === selectedDate)) {
      const blockMap = schedulesByAsset.get(row.battery_configuration_id) || new Map<number, BessSchedule>();
      blockMap.set(row.time_block, row);
      schedulesByAsset.set(row.battery_configuration_id, blockMap);
    }

    for (const row of this.socLogs().filter((item) => item.log_date === selectedDate)) {
      const blockMap = logsByAsset.get(row.battery_configuration_id) || new Map<number, BessSocLog>();
      blockMap.set(row.time_block, row);
      logsByAsset.set(row.battery_configuration_id, blockMap);
    }

    const linkedAssetId = this.linkedAssetId();
    const orderedAssets = [...this.assets()].sort((left, right) => {
      if (left.id === linkedAssetId) return -1;
      if (right.id === linkedAssetId) return 1;
      return left.entry_id.localeCompare(right.entry_id);
    });

    return orderedAssets.map((asset, assetIndex) => {
      const scheduleMap = schedulesByAsset.get(asset.id) || new Map<number, BessSchedule>();
      const logMap = logsByAsset.get(asset.id) || new Map<number, BessSocLog>();
      const forecastAsset = forecastAssets.get(asset.id);
      const forecastBlocks = new Map<number, BessForecastBlock>((forecastAsset?.blocks || []).map((block) => [block.block_number, block]));
      const energyCapacity = this.toNumber(asset.energy_capacity_mw) || 1;
      const powerCapacity = this.toNumber(asset.power_capacity_mw) || 0;
      const floorSoc = this.toNumber(asset.soc_final);
      let rollingSoc = forecastAsset ? this.toNumber(forecastAsset.starting_soc) : this.findInitialSoc(asset, logMap);

      const cells = this.blocks().map((block) => {
        const priorSoc = rollingSoc;
        const forecastBlock = forecastBlocks.get(block);
        const schedule = scheduleMap.get(block);
        const log = logMap.get(block);
        const activeForecastBlock = schedule ? null : forecastBlock;
        if (activeForecastBlock) {
          rollingSoc = this.toNumber(activeForecastBlock.forecast_soc) || rollingSoc;
        } else if (log) {
          rollingSoc = this.toNumber(log.soc_after) || rollingSoc;
        } else if (schedule) {
          const energy = this.toNumber(schedule.energy_mw);
          const deltaPercent = (energy / energyCapacity) * 25;
          if (schedule.action === 'charge') {
            rollingSoc = this.clampSoc(rollingSoc + deltaPercent, asset);
          } else if (schedule.action === 'discharge') {
            rollingSoc = this.clampSoc(rollingSoc - deltaPercent, asset);
          } else {
            rollingSoc = this.clampSoc(rollingSoc, asset);
          }
        }

        let action = schedule ? this.normalizeAction(schedule.action) : activeForecastBlock ? this.normalizeAction(activeForecastBlock.action) : 'idle';
        let bidPlaced = schedule ? this.hasSubmittedBidStatus(schedule.status) : false;
        if (!activeForecastBlock && !schedule) {
          action = this.suggestMarketAction(block, rollingSoc, floorSoc, threshold, selectedForecastProduct, lowBand, highBand, thresholdBenefits);
          bidPlaced = false;
        }

        if (!this.meetsThresholdBenefit(action, block, threshold, thresholdBenefits)) {
          action = 'idle';
          bidPlaced = false;
        }

        const power = action === 'idle'
          ? null
          : schedule
            ? this.toNumber(schedule.energy_mw)
            : activeForecastBlock
              ? this.toNumber(activeForecastBlock.forecast_mw)
              : Math.round(powerCapacity * 0.34);

        if (!activeForecastBlock && !schedule) {
          const deltaPercent = ((Math.abs(power || 0) || 0) / energyCapacity) * 25;
          if (action === 'charge') {
            rollingSoc = this.clampSoc(rollingSoc + deltaPercent, asset);
          } else if (action === 'discharge') {
            rollingSoc = this.clampSoc(rollingSoc - deltaPercent, asset);
          } else {
            rollingSoc = this.clampSoc(rollingSoc, asset);
          }
        }

        const resolvedSoc = activeForecastBlock ? this.toNumber(activeForecastBlock.forecast_soc) : Number(rollingSoc.toFixed(2));
        const resolvedAction = this.resolveCellAction(action, priorSoc, resolvedSoc, power);
        return {
          block,
          label: this.blockLabel(block),
          soc: resolvedSoc,
          power,
          action: resolvedAction,
          trend: this.resolveTrend(priorSoc, resolvedSoc),
          bidPlaced,
          status: schedule
            ? this.statusSymbol(schedule.status)
            : activeForecastBlock
              ? `${Math.round(this.toNumber(activeForecastBlock.confidence) * 100)}%`
              : this.syntheticStatusSymbol(action, block, assetIndex, bidPlaced),
          source: activeForecastBlock ? ('forecast' as const) : ('actual' as const),
        };
      });

      const chargeLakh =
        cells.reduce(
          (sum, cell) => sum + (cell.action === 'charge' ? (this.bandValue(selectedForecastProduct, cell.block) || 0) * Math.abs(cell.power || 0) : 0),
          0,
        ) / 100;
      const revenueLakh =
        cells.reduce(
          (sum, cell) => sum + (cell.action === 'discharge' ? (this.bandValue(selectedForecastProduct, cell.block) || 0) * Math.abs(cell.power || 0) : 0),
          0,
        ) / 100;
      const totalChargeLakh = Number(revenueLakh.toFixed(1));
      const totalRevenueLakh = Number(chargeLakh.toFixed(1));
      const totalProfitLakh = Number((chargeLakh - revenueLakh  ).toFixed(1));
      const fallbackSoc = this.safeSocValue(rollingSoc, 0);
      const socSeries = cells.map((cell) => this.safeSocValue(cell.soc, fallbackSoc));
      const currentBlockSoc = this.safeSocValue(cells[Math.max(0, this.currentBlock() - 1)]?.soc, fallbackSoc);
      return {
        asset,
        vendorLabel: asset.plant_type || `Asset ${asset.entry_id}`,
        currentSoc: currentBlockSoc,
        currentBlockSoc,
        socStart: socSeries[0] ?? fallbackSoc,
        socEnd: socSeries[socSeries.length - 1] ?? fallbackSoc,
        socTrendPoints: this.buildSocTrendPoints(socSeries),
        socTrendAreaPath: this.buildSocTrendAreaPath(socSeries),
        totalChargeLakh,
        totalRevenueLakh,
        totalProfitLakh,
        cells,
      };
    });
  });

  constructor() {
    effect(() => {
      this.scrollRequestVersion();
      const scrollHost = this.matrixScroll()?.nativeElement;
      if (!scrollHost) {
        return;
      }
      if (this.selectedProduct !== 'INTRADAY') {
        queueMicrotask(() => scrollHost.scrollTo({ left: 0, behavior: 'auto' }));
        return;
      }
      const target = this.timeBlockHeaders()[this.currentBlock() - 1]?.nativeElement;
      if (!target) {
        return;
      }
      queueMicrotask(() => this.scrollToCurrentBlock(scrollHost, target));
    });
    this.hydrateFromQueryParams();
    this.load();
  }

  protected onProductChange(value: ProductType): void {
    this.selectedProduct = value;
    this.syncSelectedDate();
    this.filtersVersion.update((current) => current + 1);
    this.loadForecasts();
  }

  protected onThresholdChange(value: string): void {
    this.thresholdInput = value || '';
    this.filtersVersion.update((current) => current + 1);
  }

  protected onDateChange(value: string): void {
    this.selectedDate = this.selectedProduct === 'INTRADAY' ? this.todayIsoDate() : value || this.todayIsoDate();
    this.filtersVersion.update((current) => current + 1);
    this.loadForecasts();
  }

  protected refreshForecastPrices(forceRefetch = true): void {
    this.syncForecastPrices(this.selectedDate, forceRefetch, true);
  }

  protected toggleAssetGraph(assetId: string): void {
    this.linkedAssetId.update((current) => (current === assetId ? null : assetId));
  }

  protected isAssetGraphVisible(assetId: string): boolean {
    return this.linkedAssetId() === assetId;
  }

  protected trackAssetRow(_: number, row: AssetMatrixRow): string {
    return row.asset.id;
  }

  protected trackCell(_: number, cell: MatrixCell): number {
    return cell.block;
  }

  protected statusGlyph(cell: MatrixCell): string {
    if (cell.trend === 'up') return '↗';
    if (cell.trend === 'down') return '↘';
    return '–';
  }

  protected formatSoc(value: number | null): string {
    return value !== null ? `${Number(value).toFixed(2).replace(/\.00$/, '')}%` : '-';
  }

  protected formatPower(value: number | null): string {
    return value !== null ? Number(value).toFixed(2).replace(/\.00$/, '') : '-';
  }

  protected tooltipActionLabel(cell: MatrixCell): string {
    return cell.action === 'charge' ? 'CHARGING' : 'DISCHARGING';
  }

  protected tooltipPriceLabel(cell: MatrixCell): string {
    const market = this.selectedMarketProduct();
    const price = cell.action === 'idle' ? null : this.bandValue(market, cell.block);

    return price === null ? '-' : `${price.toFixed(4)} Rs.`;
  }

  protected tooltipQuantityLabel(cell: MatrixCell): string {
    return cell.power === null ? '-' : `${Math.abs(cell.power).toFixed(0)} MW`;
  }

  protected showCellTooltip(event: MouseEvent, cell: MatrixCell): void {
    if (cell.action === 'idle') {
      this.hoveredTooltip.set(null);
      return;
    }
    this.hoveredTooltip.set(this.buildTooltip(event, cell));
  }

  protected moveCellTooltip(event: MouseEvent, cell: MatrixCell): void {
    if (cell.action === 'idle') {
      this.hoveredTooltip.set(null);
      return;
    }
    this.hoveredTooltip.set(this.buildTooltip(event, cell));
  }

  protected hideCellTooltip(): void {
    this.hoveredTooltip.set(null);
  }

  protected batteryFillHeight(value: number | null): number {
    if (value === null || !Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(100, Number(value)));
  }

  protected cellAreaPath(cells: MatrixCell[], block: number, action: 'charge' | 'discharge' | 'idle'): string {
    const index = Math.max(0, Math.min(cells.length - 1, block - 1));
    const { min, max } = this.cellSocBounds(cells);
    const points = [
      { x: 0, y: this.cellAreaY(cells[Math.max(0, index - 1)]?.soc, min, max) },
      { x: 14, y: this.cellAreaY(cells[index]?.soc, min, max) },
      { x: 28, y: this.cellAreaY(cells[Math.min(cells.length - 1, index + 1)]?.soc, min, max) },
    ];
    if (action === 'discharge') {
      return `M${points[0].x} 1 L${points[0].x} ${points[0].y} L${points[1].x} ${points[1].y} L${points[2].x} ${points[2].y} L${points[2].x} 1 Z`;
    }
    return `M${points[0].x} 17 L${points[0].x} ${points[0].y} L${points[1].x} ${points[1].y} L${points[2].x} ${points[2].y} L${points[2].x} 17 Z`;
  }

  protected cellLinePath(cells: MatrixCell[], block: number): string {
    const index = Math.max(0, Math.min(cells.length - 1, block - 1));
    const { min, max } = this.cellSocBounds(cells);
    const points = [
      { x: 0, y: this.cellAreaY(cells[Math.max(0, index - 1)]?.soc, min, max) },
      { x: 14, y: this.cellAreaY(cells[index]?.soc, min, max) },
      { x: 28, y: this.cellAreaY(cells[Math.min(cells.length - 1, index + 1)]?.soc, min, max) },
    ];
    return `M${points[0].x} ${points[0].y} L${points[1].x} ${points[1].y} L${points[2].x} ${points[2].y}`;
  }

  protected blockLabel(block: number): string {
    const minutes = (block - 1) * 15;
    const startHour = Math.floor(minutes / 60);
    const startMinute = minutes % 60;
    const endMinutes = minutes + 15;
    const endHour = Math.floor(endMinutes / 60) % 24;
    const endMinute = endMinutes % 60;
    return `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}-${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
  }

  private load(): void {
    this.loading.set(true);
    this.batteryService.list().subscribe({
      next: (assets) => {
        this.assets.set(assets);
        this.operationsService.listSchedules().subscribe({
          next: (schedules) => {
            this.schedules.set(schedules);
            this.operationsService.listSocLogs().subscribe({
              next: (logs) => {
                this.socLogs.set(logs);
                this.loadForecasts();
              },
              error: () => {
                this.socLogs.set([]);
                this.toast.show('Failed to load BESS SOC logs', '', 'danger');
                this.loadForecasts();
              },
            });
          },
          error: () => {
            this.schedules.set([]);
            this.socLogs.set([]);
            this.loading.set(false);
            this.toast.show('Failed to load BESS schedules', '', 'danger');
          },
        });
      },
      error: () => {
        this.assets.set([]);
        this.schedules.set([]);
        this.socLogs.set([]);
        this.forecasts.set(null);
        this.loading.set(false);
        this.toast.show('Failed to load battery assets', '', 'danger');
      },
    });
  }

  private loadForecasts(): void {
    const requestDate = this.selectedProduct === 'INTRADAY' ? this.todayIsoDate() : this.selectedDate || this.todayIsoDate();
    this.selectedDate = requestDate;
    this.operationsService.listForecasts(this.selectedMarketProduct(), requestDate).subscribe({
      next: (response) => {
        this.forecasts.set(response);
        this.selectedDate = this.selectedProduct === 'INTRADAY'
          ? this.todayIsoDate()
          : response.forecast_date || requestDate;
        this.syncForecastPrices(this.selectedDate, false);
      },
      error: () => {
        this.forecasts.set(null);
        this.syncForecastPrices(requestDate, false, false, 'Failed to load BESS forecasts');
      },
    });
  }

  private resolveCurrentBlock(): number {
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    return Math.min(96, Math.max(1, Math.floor(minutes / 15) + 1));
  }

  private buildMarketBands(): Array<{ key: 'DAM' | 'GDAM' | 'RTM'; values: string[] }> {
    return ['DAM', 'GDAM', 'RTM'].map((key) => ({
      key: key as 'DAM' | 'GDAM' | 'RTM',
      values: this.blocks().map((block) => this.formatBandValue(this.bandValue(key as 'DAM' | 'GDAM' | 'RTM', block))),
    }));
  }

  private selectedMarketProduct(): 'DAM' | 'RTM' {
    return this.selectedProduct === 'DAY_AHEAD' ? 'DAM' : 'RTM';
  }

  private marketBandThreshold(product: 'DAM' | 'RTM', percentile: number): number | null {
    const values = this.marketPrices()[product].filter((value): value is number => Number.isFinite(value));
    if (values.length === 0) {
      return null;
    }
    const ordered = [...values].sort((left, right) => left - right);
    const index = Math.min(ordered.length - 1, Math.max(0, Math.floor((ordered.length - 1) * percentile)));
    return ordered[index];
  }

  private suggestMarketAction(
    block: number,
    currentSoc: number,
    floorSoc: number,
    threshold: number | null,
    market: 'DAM' | 'RTM',
    lowBand: number | null,
    highBand: number | null,
    thresholdBenefits: { charge: number[]; discharge: number[] },
  ): 'charge' | 'discharge' | 'idle' {
    const marketPrice = this.bandValue(market, block);
    if (marketPrice === null) {
      return 'idle';
    }

    const canCharge = currentSoc < 98;
    const canDischarge = currentSoc > floorSoc + 2;

    if (threshold !== null) {
      const chargeBenefit = thresholdBenefits.charge[block - 1] ?? 0;
      const dischargeBenefit = thresholdBenefits.discharge[block - 1] ?? 0;
      const chargeEligible = canCharge && chargeBenefit >= threshold;
      const dischargeEligible = canDischarge && dischargeBenefit >= threshold;

      if (chargeEligible && dischargeEligible) {
        return dischargeBenefit >= chargeBenefit ? 'discharge' : 'charge';
      }
      if (dischargeEligible) {
        return 'discharge';
      }
      if (chargeEligible) {
        return 'charge';
      }
      return 'idle';
    }

    const chargeThreshold = lowBand;
    const dischargeThreshold = highBand;

    if (canDischarge && dischargeThreshold !== null && marketPrice >= dischargeThreshold) {
      return 'discharge';
    }
    if (canCharge && chargeThreshold !== null && marketPrice <= chargeThreshold) {
      return 'charge';
    }
    return 'idle';
  }

  private meetsThresholdBenefit(
    action: 'charge' | 'discharge' | 'idle',
    block: number,
    threshold: number | null,
    thresholdBenefits: { charge: number[]; discharge: number[] },
  ): boolean {
    if (threshold === null || action === 'idle') {
      return true;
    }
    const benefit = action === 'charge'
      ? thresholdBenefits.charge[block - 1] ?? 0
      : thresholdBenefits.discharge[block - 1] ?? 0;
    return benefit >= threshold;
  }

  private buildThresholdBenefits(market: 'DAM' | 'RTM'): { charge: number[]; discharge: number[] } {
    const values = this.blocks().map((block) => this.bandValue(market, block));

    const charge = Array.from({ length: values.length }, () => 0);
    const discharge = Array.from({ length: values.length }, () => 0);

    let bestFuturePrice = Number.NEGATIVE_INFINITY;
    for (let index = values.length - 1; index >= 0; index -= 1) {
      const value = values[index];
      if (value !== null) {
        bestFuturePrice = Math.max(bestFuturePrice, value);
      }
      charge[index] = value !== null && Number.isFinite(bestFuturePrice) ? Number((bestFuturePrice - value).toFixed(2)) : 0;
    }

    let cheapestPastPrice = Number.POSITIVE_INFINITY;
    for (let index = 0; index < values.length; index += 1) {
      const value = values[index];
      if (value !== null) {
        cheapestPastPrice = Math.min(cheapestPastPrice, value);
      }
      discharge[index] = value !== null && Number.isFinite(cheapestPastPrice) ? Number((value - cheapestPastPrice).toFixed(2)) : 0;
    }

    return { charge, discharge };
  }

  private bandValue(key: 'DAM' | 'GDAM' | 'RTM', block: number): number | null {
    const liveValues = this.marketPrices()[key];
    const liveValue = liveValues?.[block - 1];
    if (Number.isFinite(liveValue)) {
      return Number(liveValue);
    }
    return null;
  }

  private syntheticBandValue(key: 'DAM' | 'GDAM' | 'RTM', block: number): number {
    const hour = Math.floor((block - 1) / 4);
    const quarter = (block - 1) % 4;
    const middayBump = hour >= 7 && hour <= 9 ? 5.2 : hour >= 18 && hour <= 21 ? 2.1 : 0;
    const base = this.selectedProduct === 'INTRADAY' ? 2.05 : 2.55;
    const bandOffset = key === 'DAM' ? 0.06 : key === 'GDAM' ? 0.12 : 0;
    const pattern = [0.0, 0.18, 0.4, 0.28][quarter];
    return Number((base + bandOffset + (hour * 0.19) % 2.8 + middayBump + pattern).toFixed(2));
  }

  private syncForecastPrices(
    selectedDate: string,
    forceRefetch = false,
    notifySuccess = false,
    upstreamErrorMessage?: string,
  ): void {
    const products: MarketProduct[] = ['DAM', 'GDAM', 'RTM'];
    this.syncingForecastPrices.set(true);

    forkJoin(
      products.map((product) =>
        this.operationsService.autoFetchForecastPrice(product, selectedDate, forceRefetch).pipe(
          catchError(() => of(null)),
        ),
      ),
    ).subscribe({
      next: () => {
        this.loadMarketPrices(selectedDate, notifySuccess, upstreamErrorMessage);
      },
      error: () => {
        this.loadMarketPrices(selectedDate, notifySuccess, upstreamErrorMessage);
      },
    });
  }

  private loadMarketPrices(selectedDate: string, notifySuccess = false, upstreamErrorMessage?: string): void {
    const products: MarketProduct[] = ['DAM', 'GDAM', 'RTM'];
    forkJoin(
      products.map((product) =>
        this.operationsService.listForecastPrices(product, selectedDate).pipe(
          map((snapshots) => this.pickMatchingForecastPrice(snapshots, selectedDate)),
          map((snapshot) => snapshot?.id || null),
          map((snapshotId) => [product, snapshotId] as const),
          catchError(() => of([product, null] as const)),
        ),
      ),
    ).subscribe({
      next: (snapshotEntries) => {
        forkJoin(
          snapshotEntries.map(([product, snapshotId]) =>
            snapshotId
              ? this.operationsService.getForecastPrice(snapshotId).pipe(
                  map((detail) => [product, this.buildPriceSeries(product, detail)] as const),
                  catchError(() => of([product, this.emptyPriceSeries()] as const)),
                )
              : of([product, this.emptyPriceSeries()] as const),
          ),
        ).subscribe({
          next: (entries) => {
            this.marketPrices.set({
              DAM: entries.find(([product]) => product === 'DAM')?.[1] || [],
              GDAM: entries.find(([product]) => product === 'GDAM')?.[1] || [],
              RTM: entries.find(([product]) => product === 'RTM')?.[1] || [],
            });
            this.lastForecastPriceSyncLabel.set(this.formatSyncLabel(new Date()));
            this.syncingForecastPrices.set(false);
            this.loading.set(false);
            this.filtersVersion.update((current) => current + 1);
            this.requestCurrentBlockScroll();
            if (upstreamErrorMessage) {
              this.toast.show(upstreamErrorMessage, '', 'danger');
            } else if (notifySuccess) {
              this.toast.show('Forecast prices synced for DAM, GDAM, and RTM', '', 'success');
            }
          },
          error: () => {
            this.marketPrices.set({
              DAM: this.emptyPriceSeries(),
              GDAM: this.emptyPriceSeries(),
              RTM: this.emptyPriceSeries(),
            });
            this.syncingForecastPrices.set(false);
            this.loading.set(false);
            this.filtersVersion.update((current) => current + 1);
            this.requestCurrentBlockScroll();
            this.toast.show(upstreamErrorMessage || 'Failed to load forecast prices', '', 'danger');
          },
        });
      },
      error: () => {
        this.syncingForecastPrices.set(false);
        this.loading.set(false);
        this.toast.show(upstreamErrorMessage || 'Failed to load forecast prices', '', 'danger');
      },
    });
  }

  private pickMatchingForecastPrice(snapshots: ForecastPriceSnapshot[], selectedDate: string): ForecastPriceSnapshot | null {
    return snapshots.find((snapshot) => snapshot.forecast_date === selectedDate) || snapshots[0] || null;
  }

  private buildPriceSeries(_product: MarketProduct, detail: ForecastPriceSnapshotDetail | null): Array<number | null> {
    const fallback = this.emptyPriceSeries();
    if (!detail) {
      return fallback;
    }

    const byBlock = new Map<number, number>();
    for (const row of detail.blocks || []) {
      const blockNumber = Number(row.block_number);
      const value = this.toNumber(row.market_price);
      if (Number.isFinite(blockNumber) && blockNumber >= 1 && blockNumber <= 96 && Number.isFinite(value) && value >= 0) {
        byBlock.set(blockNumber, value);
      }
    }

    return this.blocks().map((block, index) => byBlock.get(block) ?? fallback[index]);
  }

  private emptyPriceSeries(): Array<number | null> {
    return this.blocks().map(() => null);
  }

  private formatBandValue(value: number | null): string {
    return value === null ? '-' : value.toFixed(2).replace(/\.00$/, '');
  }

  private buildTooltip(event: MouseEvent, cell: MatrixCell): MatrixTooltip {
    const tooltipWidth = 158;
    const tooltipHeight = 124;
    const margin = 12;
    const desiredLeft = event.clientX - (tooltipWidth / 2);
    const desiredTop = event.clientY - tooltipHeight - 14;

    return {
      actionLabel: this.tooltipActionLabel(cell),
      priceLabel: this.tooltipPriceLabel(cell),
      quantityLabel: this.tooltipQuantityLabel(cell),
      socLabel: this.formatSoc(cell.soc),
      left: Math.max(margin, Math.min(window.innerWidth - tooltipWidth - margin, desiredLeft)),
      top: desiredTop < margin
        ? Math.min(window.innerHeight - tooltipHeight - margin, event.clientY + 18)
        : desiredTop,
    };
  }

  private formatSyncLabel(value: Date): string {
    return value.toLocaleString([], {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private findInitialSoc(asset: BatteryConfiguration, logMap: Map<number, BessSocLog>): number {
    const orderedLogs = [...logMap.values()].sort((left, right) => left.time_block - right.time_block);
    if (orderedLogs.length > 0) {
      return this.toNumber(orderedLogs[0].soc_before) || this.toNumber(asset.soc_initial) || 0;
    }
    return this.toNumber(asset.soc_initial) || 0;
  }

  private clampSoc(value: number, asset: BatteryConfiguration): number {
    const lower = this.toNumber(asset.soc_final);
    return Math.max(lower, Math.min(100, value));
  }

  private normalizeAction(action: string | undefined): 'charge' | 'discharge' | 'idle' {
    const normalized = (action || '').toLowerCase();
    if (normalized === 'charge') return 'charge';
    if (normalized === 'discharge') return 'discharge';
    return 'idle';
  }

  private resolveCellAction(
    action: 'charge' | 'discharge' | 'idle',
    previousSoc: number | null | undefined,
    currentSoc: number | null | undefined,
    power: number | null,
  ): 'charge' | 'discharge' | 'idle' {
    if ((power ?? 0) === 0) {
      return 'idle';
    }

    const trend = this.resolveTrend(previousSoc, currentSoc);
    if (trend === 'up') return 'charge';
    if (trend === 'down') return 'discharge';
    return action;
  }

  private statusSymbol(status: string | undefined): string {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'executed') return '↗';
    if (normalized === 'submitted' || normalized === 're-submitted' || normalized === 'resubmitted' || normalized === 'approved') return '✓';
    return '•';
  }

  private hasSubmittedBidStatus(status: string | undefined): boolean {
    const normalized = (status || '').toLowerCase();
    return normalized === 'submitted' || normalized === 're-submitted' || normalized === 'resubmitted' || normalized === 'approved' || normalized === 'executed';
  }

  private syntheticStatusSymbol(action: 'charge' | 'discharge' | 'idle', block: number, assetIndex: number, bidPlaced: boolean): string {
    if (action === 'idle') return '–';
    if (!bidPlaced) return '•';
    return (block + assetIndex) % 3 === 0 ? '↗' : '✓';
  }

  private toNumber(value: string | number | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private safeSocValue(value: number | null | undefined, fallback: number): number {
    return Number.isFinite(value) ? Number(value) : fallback;
  }

  private resolveTrend(previousSoc: number | null | undefined, currentSoc: number | null | undefined): 'up' | 'down' | 'flat' {
    const previous = this.safeSocValue(previousSoc, 0);
    const current = this.safeSocValue(currentSoc, previous);
    if (current - previous > 0.05) return 'up';
    if (previous - current > 0.05) return 'down';
    return 'flat';
  }

  private cellSocBounds(cells: MatrixCell[]): { min: number; max: number } {
    const values = cells.map((cell) => this.toNumber(cell.soc));
    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    const padding = Math.max(3, (rawMax - rawMin) * 0.12);
    return {
      min: Math.max(0, rawMin - padding),
      max: Math.min(100, rawMax + padding),
    };
  }

  private cellAreaY(value: number | null, min = 0, max = 100): number {
    const soc = Math.max(min, Math.min(max, this.toNumber(value)));
    const minY = 2;
    const maxY = 15;
    const range = Math.max(1, max - min);
    return Number((maxY - (((soc - min) / range) * (maxY - minY))).toFixed(2));
  }

  private buildSocTrendPoints(series: number[]): string {
    const sanitizedSeries = series.filter((value) => Number.isFinite(value));
    if (sanitizedSeries.length === 0) {
      return '0,23 160,23';
    }
    const width = 160;
    const height = 46;
    const rawMax = Math.max(...sanitizedSeries);
    const rawMin = Math.min(...sanitizedSeries);
    const padding = Math.max(4, (rawMax - rawMin) * 0.18);
    const maxSoc = Math.min(100, rawMax + padding);
    const minSoc = Math.max(0, rawMin - padding);
    const range = Math.max(8, maxSoc - minSoc);
    return sanitizedSeries
      .map((value, index) => {
        const x = (index / Math.max(1, sanitizedSeries.length - 1)) * width;
        const y = height - (((value - minSoc) / range) * (height - 6)) - 3;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(' ');
  }

  private buildSocTrendAreaPath(series: number[]): string {
    if (series.length === 0) {
      return 'M0 46 L160 46 L160 46 L0 46 Z';
    }
    const points = this.buildSocTrendPoints(series)
      .split(' ')
      .filter(Boolean);
    if (points.length < 2) {
      return 'M0 46 L160 46 L160 46 L0 46 Z';
    }
    return `M0 46 L${points.join(' L')} L160 46 Z`;
  }

  private todayIsoDate(): string {
    return this.formatLocalIsoDate(new Date());
  }

  private syncSelectedDate(): void {
    const dateValue = new Date();
    if (this.selectedProduct === 'DAY_AHEAD') {
      dateValue.setDate(dateValue.getDate() + 1);
    }
    this.selectedDate = this.formatLocalIsoDate(dateValue);
  }

  private formatLocalIsoDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private hydrateFromQueryParams(): void {
    const params = this.route.snapshot.queryParamMap;
    const product = params.get('product');
    const date = params.get('date');
    const assetId = params.get('assetId');

    if (product === 'DAY-AHEAD') {
      this.selectedProduct = 'DAY_AHEAD';
    } else if (product === 'INTRADAY') {
      this.selectedProduct = 'INTRADAY';
    }

    if (this.selectedProduct === 'INTRADAY') {
      this.selectedDate = this.todayIsoDate();
    } else if (date) {
      this.selectedDate = date;
    } else {
      this.syncSelectedDate();
    }

    this.linkedAssetId.set(assetId || null);
  }

  private requestCurrentBlockScroll(): void {
    this.scrollRequestVersion.update((current) => current + 1);
  }

  private scrollToCurrentBlock(scrollHost: HTMLDivElement, target: HTMLElement): void {
    const stickyOffset =
      BessFleetOperationsMatrixPage.SERIAL_COLUMN_WIDTH +
      BessFleetOperationsMatrixPage.ASSET_COLUMN_WIDTH +
      BessFleetOperationsMatrixPage.DIVIDER_COLUMN_WIDTH;
    const nextLeft = Math.max(0, target.offsetLeft - stickyOffset);
    scrollHost.scrollTo({ left: nextLeft, behavior: 'smooth' });
  }
}
